import React, { useState, useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import Sidebar from './components/Sidebar'
import Header from './components/Header';
import ProtectedRoute from './components/ProtectedRoute';
import SignupPage from './components/Authentication/SignUp.jsx';
import LoginPage from './components/Authentication/Login.jsx';
import ForgotPasswordPage from './pages/ForgotPassword.jsx';
import ResetPasswordPage from './pages/ResetPassword.jsx';
import LandingPage from './pages/LandingPage.jsx';
import About from './pages/About.jsx';
import Contact from './pages/Contact.jsx';
import { useAuth } from './hooks/useAuth';
import * as Pages from './pages/index.js'
import OrderDetailsPage from './pages/orderDetailsPage/index.jsx';
import ShipmentsPage from './pages/shipmentsPage/index.jsx';
import CreateOrder from './pages/CreateOrder.jsx';

import KYCGuard from './components/KYCGuard';
import { useLocation } from 'react-router-dom';

// Layout component for protected routes
const DashboardLayout = ({ isSidebarOpen, setIsSidebarOpen, children }) => {
  const location = useLocation();
  const exemptRoutes = ['/settings', '/support', '/privacy-policy', '/terms-conditions', '/packaging-guide', '/restricted-items'];
  const requireKYC = !exemptRoutes.includes(location.pathname);

  // Hover expands the sidebar; the header toggle button pins it open/closed.
  // Lifted here so the content column can keep its padding in sync with the
  // fixed, full-viewport-height sidebar.
  const [isSidebarHovered, setIsSidebarHovered] = useState(false);
  const isExpanded = isSidebarOpen || isSidebarHovered;

  // When the pinned state changes, drop the hover state so the toggle
  // button takes effect immediately even while the pointer is over the sidebar.
  useEffect(() => {
    setIsSidebarHovered(false);
  }, [isSidebarOpen]);

  const sidebarPadClass = isExpanded ? "pl-[150px] lg:pl-44 xl:pl-64" : "pl-14 xl:pl-20";

  return (
    <div className="min-h-screen">
      {/* Full-viewport background layer: fixed inset-0 spans the visible
          viewport by construction, immune to vh miscalculation under the
          site's global zoom (same approach as the fixed sidebar). */}
      <div className="fixed inset-0 -z-10 bg-gray-100" aria-hidden="true" />
      <Sidebar isSidebarOpen={isSidebarOpen} isExpanded={isExpanded} onHoverChange={setIsSidebarHovered} />
      <div className={`flex flex-col min-h-screen px-1 lg:px-2 xl:px-3 min-w-0 transition-all duration-300 ${sidebarPadClass}`}>
        <Header setIsSidebarOpen={setIsSidebarOpen}/>
        <main className="flex-1 flex flex-col">
          {requireKYC ? <KYCGuard>{children}</KYCGuard> : children}
        </main>
      </div>
    </div>
  );
};

function App() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { isAuthenticated, loading } = useAuth();

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1a2b4b] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <Routes>
        {/* Public Landing Page */}
        <Route 
          path="/" 
          element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <LandingPage />} 
        />
        
        {/* Public About and Contact Pages */}
        <Route 
          path="/about" 
          element={<About />} 
        />
        <Route 
          path="/contact" 
          element={<Contact />} 
        />

        <Route 
          path="/track" 
          element={<Pages.TrackOrderPage />} 
        />
        
        {/* Public Auth routes */}
        <Route 
          path="/login" 
          element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <LoginPage />} 
        />
        <Route 
          path="/signup" 
          element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <SignupPage />} 
        />

        <Route
          path="/forgot-password"
          element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <ForgotPasswordPage />}
        />

        <Route
          path="/reset-password"
          element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <ResetPasswordPage />}
        />

        {/* Protected routes - Dashboard */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardLayout isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen}>
                <Pages.DashboardPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/international-orders"
          element={
            <ProtectedRoute>
              <DashboardLayout isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen}>
                <Pages.InternationalOrdersPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/forward-orders"
          element={
            <ProtectedRoute>
              <DashboardLayout isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen}>
                <Pages.ForwardOrdersPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/reserve-orders"
          element={
            <ProtectedRoute>
              <DashboardLayout isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen}>
                <Pages.ReserveOrdersPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/pickup-requests"
          element={
            <ProtectedRoute>
              <DashboardLayout isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen}>
                <Pages.PickupRequestsPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/create-order"
          element={
            <ProtectedRoute>
              <DashboardLayout isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen}>
                <CreateOrder />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/orders"
          element={
            <ProtectedRoute>
              <DashboardLayout isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen}>
                <Pages.OrderListPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/direct-intracity-orders"
          element={
            <ProtectedRoute>
              <DashboardLayout isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen}>
                <Pages.DirectIntracityOrdersPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/exceptions-ndr"
          element={
            <ProtectedRoute>
              <DashboardLayout isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen}>
                <Pages.ExceptionsNDRPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/wallet"
          element={
            <ProtectedRoute>
              <DashboardLayout isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen}>
                <Pages.WalletPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/remittances"
          element={
            <ProtectedRoute>
              <DashboardLayout isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen}>
                <Pages.RemittancesPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/invoices"
          element={
            <ProtectedRoute>
              <DashboardLayout isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen}>
                <Pages.InvoicesPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/weight-mismatch"
          element={
            <ProtectedRoute>
              <DashboardLayout isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen}>
                <Pages.WeightMismatchPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/claims"
          element={
            <ProtectedRoute>
              <DashboardLayout isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen}>
                <Pages.ClaimsPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/createclaimpage"
          element={
            <ProtectedRoute>
              <DashboardLayout isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen}>
                <Pages.CreateClaimPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/reports"
          element={
            <ProtectedRoute>
              <DashboardLayout isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen}>
                <Pages.ReportsPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/clients"
          element={
            <ProtectedRoute>
              <DashboardLayout isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen}>
                <Pages.ClientManagementPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/scheduledreportform"
          element={
            <ProtectedRoute>
              <DashboardLayout isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen}>
                <Pages.ScheduledReportForm />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/rate-calculator"
          element={
            <ProtectedRoute>
              <DashboardLayout isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen}>
                <Pages.RateCalculatorPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/rate-card"
          element={
            <ProtectedRoute>
              <DashboardLayout isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen}>
                <Pages.RateCardPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/pincode-serviceability"
          element={
            <ProtectedRoute>
              <DashboardLayout isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen}>
                <Pages.PincodeServiceabilityPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/packaging-guide"
          element={
            <ProtectedRoute>
              <DashboardLayout isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen}>
                <Pages.PackagingGuidePage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/restricted-items"
          element={
            <ProtectedRoute>
              <DashboardLayout isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen}>
                <Pages.RestrictedItemsPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route 
          path="/terms-conditions" 
          element={<Pages.TermsAndConditionsPage />} 
        />
        <Route 
          path="/privacy-policy" 
          element={<Pages.PrivacyPolicyPage />} 
        />
                <Route
          path="/tracking"
          element={
            <ProtectedRoute>
              <DashboardLayout isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen}>
                <Pages.TrackingPage/>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/fetch-awb-numbers"
          element={
            <ProtectedRoute>
              <DashboardLayout isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen}>
                <Pages.FetchAWBNumbersPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/services"
          element={
            <ProtectedRoute>
              <DashboardLayout isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen}>
                <Pages.ServicesPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <DashboardLayout isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen}>
                <Pages.SettingsPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/support"
          element={
            <ProtectedRoute>
              <DashboardLayout isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen}>
                <Pages.SupportPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/pickup-request/:id"
          element={
            <ProtectedRoute>
              <DashboardLayout isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen}>
                <Pages.PickupRequestDetailsPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/create-pickup-request"
          element={
            <ProtectedRoute>
              <DashboardLayout isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen}>
                <Pages.CreatePickupRequestPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/order-details"
          element={
            <ProtectedRoute>
              <DashboardLayout isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen}>
                <OrderDetailsPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/shipments"
          element={
            <ProtectedRoute>
              <DashboardLayout isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen}>
                <ShipmentsPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/orders"
          element={
            <ProtectedRoute>
              <DashboardLayout isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen}>
                <Pages.OrderListPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />

        {/* Catch all - redirect to dashboard */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </BrowserRouter>
  )
}

export default App
